'use client';

import * as React from 'react';
import classNames from 'classnames';
import LoadingContent from '../LoadingContent';
import Icon, { IconsAvailable } from '../Icon';
import './styles.scss';

type OptionType = {
  label: string;
  value?: string;
  children?: OptionType[];
};

export interface IControlSelectProps {
  /** Options to render */
  options: OptionType[];
  /** placeholder */
  placeholder?: string;
  /** Size */
  size?: 'default' | 'small';
  /** Controlled value */
  value?: string;
  /** Disabled status */
  disabled?: boolean;
  /** Focus handler */
  onFocus?: React.DOMAttributes<HTMLSelectElement>['onFocus'];
  /** Blur handler */
  onBlur?: React.DOMAttributes<HTMLSelectElement>['onBlur'];
  /** Change handler */
  onChange?: (e: { target: { value: string } }) => void;
  /** Keydown handler */
  onKeyDown?: React.InputHTMLAttributes<HTMLSelectElement>['onKeyDown'];
  /** Keyup handler */
  onKeyUp?: React.InputHTMLAttributes<HTMLSelectElement>['onKeyUp'];
  /** Keyup handler */
  onClick?: React.InputHTMLAttributes<HTMLSelectElement>['onClick'];
  /** Focus field on mount */
  autoFocus?: boolean;
  /** Addon element to prepend to field */
  addOn?: React.ReactNode;
  /** Color higlight for errors */
  hasError?: boolean;
  /** className to append */
  className?: string;
  /** Pass input ref */
  selectRef?: React.RefCallback<HTMLSelectElement>;
  /** Styles to apply to wrapper */
  style?: React.CSSProperties;
  /** Styles to apply to inner input field */
  selectStyle?: React.CSSProperties;
  /** DOM target element name attribute */
  name?: string;
  /** DOM target element id attribute */
  id?: string;
  /** Loading */
  loading?: boolean;
  /** Icon to display in front of everything */
  icon?: IconsAvailable;
  /** Title when used without associated label */
  title?: string;
}

/**
 * Regular HTML input field with extended functionality and styling. Use this within form row.
 */
const ControlSelect: React.FunctionComponent<IControlSelectProps> = (props) => {
  const [hasFocus, setHasFocus] = React.useState(false);
  const inputRef = React.useRef<HTMLSelectElement | null>(null);

  return (
    <div
      className={classNames(
        'control-select',
        {
          ['has-error']: props.hasError,
          ['has-value']: props.value && props.value.length > 0,
          ['focus']: hasFocus,
          ['disabled']: props.disabled,
          ['size-default']: props.size === 'default' || !props.size,
          ['size-small']: props.size === 'small',
        },
        props.className,
      )}
      style={props.style}
      onClick={(e) => {
        e.preventDefault();
        if (inputRef.current && !hasFocus) {
          inputRef.current.focus();
        }
      }}
      role="button"
    >
      {props.icon && (
        <Icon
          width={24}
          height={24}
          kind={props.icon}
          wrapperClassName={'control-select__icon'}
        />
      )}
      {props.addOn && <span className={'control-select__addon'}>{props.addOn}</span>}
      <div className={'control-select__holder'}>
        <select
          style={props.selectStyle}
          className={'control-select__element'}
          value={props.value || ''}
          disabled={props.disabled}
          onFocus={(e) => {
            setHasFocus(true);
            if (props.onFocus) {
              props.onFocus(e);
            }
          }}
          onBlur={(e) => {
            setHasFocus(false);
            if (props.onBlur) {
              props.onBlur(e);
            }
          }}
          onChange={props.onChange}
          onKeyDown={props.onKeyDown}
          onKeyUp={props.onKeyUp}
          onClick={props.onClick}
          // placeholder={props.placeholder} todo :: @Allan
          spellCheck={false}
          ref={(e) => {
            if (props.selectRef) {
              props.selectRef(e);
            }
            if (e) {
              inputRef.current = e;
              if (props.autoFocus) {
                window.requestAnimationFrame(() => {
                  e.focus();
                });
              }
            }
          }}
          name={props.name}
          id={props.id}
          title={props.title}
          aria-busy={props.loading}
        >
          {props.options.map((option, optionIndex) => {
            if (option.children && option.children.length > 0) {
              return (
                <optgroup
                  label={option.label}
                  key={optionIndex}
                >
                  {option.children.map((sub, subIndex) => {
                    return (
                      <option
                        label={sub.label}
                        value={sub.value}
                        key={subIndex}
                      >
                        {}
                      </option>
                    );
                  })}
                </optgroup>
              );
            }
            return (
              <option
                label={option.label}
                value={option.value}
                key={optionIndex}
              >
                {}
              </option>
            );
          })}
        </select>
      </div>
      {props.loading && (
        <span className={'control-select__loader'}>
          <LoadingContent size="tiny" />
        </span>
      )}
      <Icon
        width={20}
        height={20}
        kind="arrow03-down"
        wrapperClassName={'control-select__arrow'}
      />
    </div>
  );
};

ControlSelect.displayName = 'ControlSelect';

export default ControlSelect;
